import React from 'react';
import * as Sentry from '@sentry/react';
import {useTranslation} from 'react-i18next';
import {
  StripeCardElementChangeEvent,
  StripeElementLocale,
  StripeError,
} from '@stripe/stripe-js';
import {StripeCardElementProps} from './StripeCardElement';
import {
  CardElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

export type DoConfirmType = (clientSecret: string) => Promise<any>;

export type CardBrand = StripeCardElementChangeEvent['brand'];
export type UseStripeCardElementReturn = {
  confirmDepositPaymentIntent: DoConfirmType;
  confirmPaymentIntent: DoConfirmType;
  brand: CardBrand;
  disabled: boolean;
  cardProps: StripeCardElementProps;
  splitCardElementsProps: StripeCardElementProps;
};

function useStripeCardElement(
  onError: (error: StripeError) => void,
  isSplit = false,
  extraFields?: Record<string, unknown>,
): UseStripeCardElementReturn {
  const elements = useElements();
  const stripe = useStripe();
  const {i18n} = useTranslation();
  const [disabled, setDisabled] = React.useState(true);
  const [brand, setBrand] = React.useState<CardBrand>('unknown');
  const [error, setError] = React.useState('');
  const depositErrorMessage = 'Unable to make a deposit by card. ';
  const paymentErrorMessage = 'Unable to confirm card payment. ';

  const confirmPaymentIntent = React.useCallback(
    async (clientSecret: string) => {
      if (!stripe) {
        const errorMsg = paymentErrorMessage + 'Stripe instance is missing.';
        setError(errorMsg);
        Sentry.captureMessage(errorMsg);
        return;
      }

      const cardElement = isSplit
        ? elements?.getElement(CardNumberElement)
        : elements?.getElement(CardElement);

      if (!cardElement) {
        const errorMsg = paymentErrorMessage + 'Card element is missing.';
        setError(errorMsg);
        Sentry.captureMessage(errorMsg);
        return;
      }

      setError('');
      const payload = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: extraFields,
        },
        setup_future_usage: 'off_session',
      });

      if (payload.error) {
        onError(payload.error);
      }

      return payload;
    },
    [elements, extraFields, isSplit, onError, stripe],
  );

  const confirmDepositPaymentIntent = React.useCallback(
    async (clientSecret: string) => {
      if (!stripe) {
        const errorMsg = depositErrorMessage + 'Stripe instance is missing.';
        setError(errorMsg);
        Sentry.captureMessage(errorMsg);
        return;
      }

      const cardElement = isSplit
        ? elements?.getElement(CardNumberElement)
        : elements?.getElement(CardElement);
      if (!cardElement) {
        const errorMsg = depositErrorMessage + 'Card element is missing.';
        setError(errorMsg);
        Sentry.captureMessage(errorMsg);
        return;
      }

      setError('');
      const payload = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: extraFields,
        },
      });

      if (payload.error) {
        onError(payload.error);
      }

      return payload;
    },
    [elements, extraFields, isSplit, onError, stripe],
  );

  React.useEffect(() => {
    const newLocale = i18n.language as StripeElementLocale;
    elements?.update({locale: newLocale});
  }, [elements, i18n.language]);

  const handleCardChange = (event: StripeCardElementChangeEvent) => {
    setDisabled(event.empty);
    if (event.brand) setBrand(event.brand);
    setError(event.error?.message || '');
  };

  // Spread these props to the <StripeCardElement /> component.
  const cardProps: StripeCardElementProps = {
    error,
    onChange: handleCardChange,
  };
  // Spread these props to the <StripeCardElement /> component.
  const splitCardElementsProps: any = {
    error,
    onChange: handleCardChange,
  };

  return {
    confirmDepositPaymentIntent,
    confirmPaymentIntent,
    brand,
    disabled,
    cardProps,
    splitCardElementsProps,
  };
}

export {useStripeCardElement};
